import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../data/siteData";
import { formatApiError } from "../lib/api";
import { Loader2, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (user?.role === "admin") navigate("/admin/dashboard", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back, Admin");
            navigate("/admin/dashboard", { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            let msg;
            if (status === 401) {
                msg = "Wrong email or password. Try again, or use Forgot Password.";
            } else if (!err?.response) {
                msg = "Network problem — couldn't reach the server. Check your connection and try again.";
            } else {
                msg = formatApiError(err.response?.data?.detail) || err.message || "Login failed.";
            }
            setErrorMsg(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] px-6 py-12" data-testid="admin-login-page">
            <Helmet>
                <title>Admin Sign In | Anand Tutorials</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <div className="text-center mb-8">
                        <img src={LOGO_URL} alt="Anand Tutorials" className="h-20 mx-auto mb-4" />
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">
                            <Lock className="w-3.5 h-3.5" /> Admin Access
                        </div>
                        <h1 className="font-heading text-2xl font-bold mt-2">Admin Sign In</h1>
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        {errorMsg && (
                            <div
                                role="alert"
                                data-testid="admin-login-error"
                                className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800"
                            >
                                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                <span>{errorMsg}</span>
                            </div>
                        )}
                        <label className="block">
                            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Email</span>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="admin-login-email"
                                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Password</span>
                            <div className="relative">
                                <input
                                    type={showPwd ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="admin-login-password"
                                    className="w-full pr-10 px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    aria-label={showPwd ? "Hide password" : "Show password"}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                                >
                                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </label>
                        <div className="text-right">
                            <Link
                                to="/admin/forgot-password"
                                data-testid="admin-forgot-password-link"
                                className="text-xs font-semibold text-[#1E3A8A] hover:underline"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="w-full bg-[#1E3A8A] text-white font-semibold py-3 rounded-lg hover:bg-[#1E40AF] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            {submitting ? "Signing in..." : "Sign In"}
                        </button>
                    </form>
                </div>
                <p className="text-center text-blue-200 text-xs mt-6">
                    © {new Date().getFullYear()} Anand Tutorials · <Link to="/" className="hover:text-amber-300">Back to Site</Link>
                </p>
            </div>
        </div>
    );
}
